/**
 * Lightweight client-side logging for "discard unsaved changes" confirmations.
 *
 * Mirrors the {@link logPlatformGating} pattern: emits a structured
 * `console.debug` line and dispatches a window-level CustomEvent so an
 * analytics layer can subscribe without this util taking on a vendor SDK
 * dependency.
 *
 * Usage (inside `useUnsavedChangesGuard.confirmDiscard`):
 *   logDiscardConfirmation({ surface: "PrivacyPermissionsDialog" });
 *
 * Subscribers:
 *   window.addEventListener(DISCARD_CONFIRMATION_EVENT_NAME, (e) => {
 *     analytics.track("discard_confirmed", e.detail);
 *   });
 */

export interface DiscardConfirmationEvent {
  /**
   * Component / dialog emitting the event — e.g. "PrivacyPermissionsDialog"
   * or "AppConfigurationDialog". Free-form so callers can be specific.
   */
  surface: string;
  /** Free-form extra context for debugging (bundleId, configId, etc.). */
  extra?: Record<string, unknown>;
}

const EVENT_NAME = "smplify:discard-confirmed";

export function logDiscardConfirmation(event: DiscardConfirmationEvent): void {
  // eslint-disable-next-line no-console
  console.debug(
    `[discard-confirmed] unsaved changes discarded on ${event.surface}`,
    {
      surface: event.surface,
      timestamp: new Date().toISOString(),
      ...(event.extra ?? {}),
    },
  );

  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(
        new CustomEvent(EVENT_NAME, {
          detail: { ...event, timestamp: Date.now() },
        }),
      );
    } catch {
      // Best-effort: never let analytics break the UI.
    }
  }
}

export const DISCARD_CONFIRMATION_EVENT_NAME = EVENT_NAME;
